import { useCallback, useEffect, useState } from 'react';
import APICall from '../../misc/APICall';
import moment from "moment/moment";

const normalizeEventsToday = (items = []) =>
  items
    .filter((item) => String(item.status_code) !== '200')
    .map((item) => ({
      job: item.job,
      status_code: item.status_code,
      run_id: item.run_id,
      statusMessage: item.statusMessage,
      start_time: item.start_time,
      end_time: item.end_time,
    }));

const normalizeLastEventPerJob = (items = []) =>
  items.map((item) => ({
    job: item.job,
    status_code: item.last_status_code,
    run_id: item.id,
    statusMessage: item.details,
    start_time: item.last_seen_at,
    end_time: null,
  }));

const normalizeOpenRuns = (items = []) =>
  items.map((item) => ({
    job: item.job,
    status_code: 'open',
    run_id: item.run_id,
    statusMessage: 'Run in progress',
    start_time: item.started_at,
    end_time: null,
  }));

const useN8nEvents = ({ isN8nOpen, access_token }) => {
  const [n8nLoading, setN8nLoading] = useState(false);
  const [n8nError, setN8nError] = useState(null);

  const [openNextCursor, setOpenNextCursor] = useState(null);
  const [failedNextCursor, setFailedNextCursor] = useState(null);
  const [openHasMore, setOpenHasMore] = useState(false);
  const [failedHasMore, setFailedHasMore] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);

  const [n8nSections, setN8nSections] = useState({
    eventsToday: [],
    lastEventPerJob: [],
    openRuns: [],
  });

  const getN8nErrorMessage = (error) =>
    error?.error ||
    error?.details?.message ||
    `Unable to load n8n events${error?.status ? ` (HTTP ${error.status})` : ''}`;

  const fetchN8nPage = useCallback(async ({ openLimit = 20, failedLimit = 20, openCursor = null, failedCursor = null, append = false } = {}) => {
    try {
      const qs = new URLSearchParams();
      qs.set('open_limit', String(openLimit));
      qs.set('failed_limit', String(failedLimit));
      if (openCursor) qs.set('open_cursor', openCursor);
      if (failedCursor) qs.set('failed_cursor', failedCursor);

      const res = await APICall({
        access_token,
        url: `n8n/events/today?${qs.toString()}`,
        verb: 'get',
      });

      const nextEventsToday = normalizeEventsToday(res.eventsToday || []);
      const nextLastSeen = normalizeLastEventPerJob(res.lastEventPerJob || []);
      const nextOpenRuns = normalizeOpenRuns(res.openRuns || []);

      setN8nSections((prev) => ({
        eventsToday: append ? [...prev.eventsToday, ...nextEventsToday] : nextEventsToday,
        lastEventPerJob: append ? prev.lastEventPerJob : nextLastSeen,
        openRuns: append ? [...prev.openRuns, ...nextOpenRuns] : nextOpenRuns,
      }));

      setOpenHasMore(Boolean(res.paging?.open?.hasMore));
      setFailedHasMore(Boolean(res.paging?.failed?.hasMore));
      setOpenNextCursor(res.paging?.open?.nextCursor || null);
      setFailedNextCursor(res.paging?.failed?.nextCursor || null);
      setLastUpdateTime(moment.now());
      setN8nError(null);
    } catch (error) {
      setN8nError(getN8nErrorMessage(error));
    }
  }, [access_token]);

  const loadN8nPage = async () => {
  try {
    await fetchN8nPage({
      openLimit: 20,
      failedLimit: 20,
      append: false,
    });
  } catch (error) {
    console.error('n8n events request failed:', {
      error: error.error,
      status: error.status,
      code: error.code,
      details: error.details,
      raw: error.err,
    });

    setN8nError(
      error.error ||
      error.details?.message ||
      `Unable to load n8n events${error.status ? ` (HTTP ${error.status})` : ''}`
    );
  }
};


  const refreshN8n = useCallback(async () => {
    setN8nLoading(true);
    await fetchN8nPage({ openLimit: 20, failedLimit: 20, append: false });
    setN8nLoading(false);
  }, [fetchN8nPage]);

  useEffect(() => {
      // Schedule a single initial fetch. Using setTimeout(0) ensures the first mount in React StrictMode
      // doesn't trigger an extra call, because the timeout is cleared during the intentional unmount/remount.
      const timeout = setTimeout(() => {
        fetchN8nPage({ openLimit: 20, failedLimit: 20, append: false });
      }, 0);

      // Periodic refresh
      const interval = setInterval(() => {
        fetchN8nPage({ openLimit: 20, failedLimit: 20, append: false });
      }, 1 * 60 * 1000); // Every 1 minute

      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
  }, [fetchN8nPage]);

  const openRuns = n8nSections.openRuns;
  const failedJobs = n8nSections.eventsToday;

  return {
    n8nLoading,
    n8nError,
    n8nSections,
    openRuns,
    failedJobs,
    latestOpenRun: openRuns[0] || null,
    latestFailure: failedJobs[0] || null,
    openHasMore,
    failedHasMore,
    openNextCursor,
    failedNextCursor,
    refreshN8n,
    lastUpdateTime
  };
};

export default useN8nEvents;
